import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box, Button, Group, Paper, Stack, Text, Title, Tooltip } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import type { GetSponsorTopicsResponse } from '@/services/api/api.types'

import { EntryMenuButton } from './EntryMenuButton'
import { OptionPill } from './OptionPill'
import { SidekickButton } from './SidekickButton'
import { SidekickSelectionModal } from './SidekickSelectionModal'
import type { SidekickOption } from './useSidekicks'
import { getSidekickId } from './utils'

// Constants for repeated styles and configurations
const INPUT_STYLES = {
  border: 'none',
  background: 'transparent',
  fontSize: 'inherit',
  width: '100%',
  padding: '2px 4px',
}

const TOOLTIP_STYLES = {
  tooltip: {
    maxWidth: '250px',
  },
}

const WARNING_TOOLTIP_PROPS = {
  withArrow: true,
  arrowSize: 12,
  position: 'top' as const,
  offset: 8,
  multiline: true,
  styles: TOOLTIP_STYLES,
  label: 'This topic is associated with one or more modules, changes may impact the experience of users in the app.',
}

const SIDEKICK_TOOLTIP_PROPS = {
  withArrow: true,
  position: 'top' as const,
  multiline: true,
  disabled: false, // Will be overridden
  styles: TOOLTIP_STYLES,
}

interface TopicEntryMenuProps {
  onMoveUp?: () => void
  onMoveDown?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  canDelete?: boolean
  canDuplicate?: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
}

interface TopicEntryProps {
  item: GetSponsorTopicsResponse[0]
  index: number
  subtopics?: { id: string; key: string; displayName: string }[]
  onUpdateSubtopicName?: (id: string, newName: string) => void
  onRemoveSubtopic?: (id?: string) => void
  eskOptions: SidekickOption[]
  selectedExpertSidekick?: string
  onExpertSidekickChange?: (topicId: string, currentEskId: string | null, newEskId: string) => void
  moduleCount: number
  onDisplayNameChange?: (id: string, newName: string) => void
  onAddSubtopic?: (topicId?: string) => void
  showWarning?: boolean
  shouldFocus?: boolean
  newSubtopicId?: string | null
  id: string
  showModuleCounts: boolean
  menuProps: TopicEntryMenuProps
  defaultMskSidekick?: SidekickOption | null
}

export function TopicEntry({
  item,
  index,
  subtopics,
  onUpdateSubtopicName,
  onRemoveSubtopic,
  eskOptions,
  selectedExpertSidekick,
  onExpertSidekickChange,
  moduleCount,
  onDisplayNameChange,
  onAddSubtopic,
  showWarning,
  shouldFocus,
  newSubtopicId,
  id,
  showModuleCounts,
  menuProps,
  defaultMskSidekick,
}: TopicEntryProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [tempName, setTempName] = useState(item.displayName)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useCheckMobileScreen()
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  // Update tempName when item.displayName changes
  useEffect(() => {
    setTempName(item.displayName)
  }, [item.displayName])

  // Add effect to handle auto-focus and text selection for new topics
  useEffect(() => {
    if (shouldFocus) {
      setIsEditing(true)
    }
  }, [shouldFocus])

  // Select all text when input becomes focused for new topics
  useEffect(() => {
    if (isEditing && shouldFocus && inputRef.current) {
      // Use setTimeout to ensure the input is fully rendered and focused
      setTimeout(() => {
        inputRef.current?.select()
      }, 0)
    }
  }, [isEditing, shouldFocus])

  // Helper function to get the selected sidekick for display
  const getSelectedSidekickForDisplay = () => {
    if (!selectedExpertSidekick) {
      return null
    }

    if (selectedExpertSidekick === 'PSK') {
      // Use the default MSK's avatar for PSK, fallback to placeholder
      const avatarId = defaultMskSidekick?.avatarId || 'placeholder'
      const name = defaultMskSidekick?.name || 'Personal Sidekick'
      return {
        title: 'Personal Sidekick',
        name: name,
        avatarId,
        isPSK: true,
      }
    }

    const expertSidekick = eskOptions.find(opt => getSidekickId(opt) === selectedExpertSidekick)
    if (expertSidekick) {
      return {
        title: 'Expert Sidekick',
        name: expertSidekick.name,
        avatarId: expertSidekick.avatarId,
        isPSK: false,
      }
    }

    return null
  }

  const selectedSidekick = getSelectedSidekickForDisplay()

  const handleDisplayNameChange = (newName: string) => {
    if (onDisplayNameChange && item.id) {
      onDisplayNameChange(item.id, newName)
    }
  }

  const handleEditComplete = () => {
    // Clear any validation errors and exit editing mode
    setValidationError(null)
    setIsEditing(false)

    if (tempName !== item.displayName) {
      handleDisplayNameChange(tempName)
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      setTempName(item.displayName)
      setValidationError(null)
      setIsEditing(false)
    }
  }

  const canEditName = true // Simplified since it always returns true
  const canChangeSidekick = useMemo(() => moduleCount === 0, [moduleCount])

  // Clear validation error when user starts typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempName(e.target.value)
    if (validationError) {
      setValidationError(null)
    }
  }

  // Reusable edit input component
  const renderEditInput = () => (
    <Box style={{ width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={tempName}
        onChange={handleInputChange}
        onBlur={handleEditComplete}
        onKeyDown={handleInputKeyDown}
        autoFocus
        style={{
          ...INPUT_STYLES,
          borderBottom: validationError ? '2px solid var(--mantine-color-red-6)' : undefined,
        }}
      />
      {validationError && (
        <Text size="xs" c="red" mt={2}>
          {validationError}
        </Text>
      )}
    </Box>
  )

  const renderSidekickSelection = () => (
    <Box style={{ width: isMobile ? '100%' : 180, flexShrink: 0 }}>
      {selectedSidekick ? (
        <Stack gap="xs">
          <SidekickButton
            selectedSidekick={selectedSidekick}
            onClick={() => canChangeSidekick && setIsModalOpen(true)}
            disabled={!canChangeSidekick}
          />
        </Stack>
      ) : (
        <Tooltip
          {...SIDEKICK_TOOLTIP_PROPS}
          label={
            !canChangeSidekick ? 'Sidekick cannot be modified when Content Modules are assigned to a Topic' : undefined
          }
          disabled={canChangeSidekick}>
          <Button variant="light" size="xs" onClick={() => setIsModalOpen(true)} disabled={!canChangeSidekick}>
            Select Sidekick
          </Button>
        </Tooltip>
      )}
    </Box>
  )

  // Common components used in both layouts
  const renderEntryMenuButton = () => (
    <EntryMenuButton
      {...menuProps}
      ref={setActivatorNodeRef}
      attributes={attributes}
      listeners={listeners}
      isDragging={isDragging}
    />
  )

  const renderIndexNumber = () => (
    <Text c="dimmed" size="lg" style={isMobile ? { minWidth: '24px' } : { textAlign: 'left' }}>
      {index + 1}
    </Text>
  )

  const renderWarningIcon = () =>
    showWarning ? (
      <Tooltip {...WARNING_TOOLTIP_PROPS}>
        <IconAlertCircle size={20} style={{ color: '#fab005', ...(isMobile && { flexShrink: 0 }) }} />
      </Tooltip>
    ) : null

  const renderModuleCount = () =>
    showModuleCounts ? (
      <Text size="sm" c="dimmed" mt={isMobile ? '2px' : undefined}>
        ({moduleCount} modules)
      </Text>
    ) : null

  const renderSubtopics = () =>
    subtopics ? (
      <Group gap="xs" pl={isMobile ? undefined : 'lg'}>
        {subtopics.map((subtopic, idx) => (
          <OptionPill
            key={subtopic.id ?? idx}
            subtopic={subtopic}
            onDelete={onRemoveSubtopic}
            onEdit={newName => onUpdateSubtopicName?.(subtopic.id, newName)}
            onAdd={() => onAddSubtopic?.(item.id)}
            canEdit
            fontSize="12px"
            shouldFocus={subtopic.id === newSubtopicId}
          />
        ))}
        <OptionPill isAddButton onAdd={() => onAddSubtopic?.(item.id)} canEdit addTooltip="Add subtopic" />
      </Group>
    ) : null

  const renderTitleSection = () => (
    <Box style={{ flex: 1 }}>
      <Title
        order={6}
        mb={isMobile ? 0 : undefined}
        onClick={() => {
          if (canEditName && !isEditing) {
            setIsEditing(true)
            setValidationError(null)
          }
        }}
        style={{ cursor: canEditName ? 'pointer' : 'default' }}
        c={!isEditing && !item.displayName ? 'dimmed' : undefined}>
        {isEditing ? renderEditInput() : item.displayName || 'Add topic name'}
      </Title>
      {isMobile && renderModuleCount()}
    </Box>
  )

  return (
    <Paper
      p="md"
      pl="xs"
      withBorder
      style={{ transform: CSS.Transform.toString(transform), transition }}
      ref={setNodeRef}>
      {isMobile ? (
        <Stack gap="sm">
          {/* Mobile layout - stacked vertically */}
          <Group gap="xs" wrap="nowrap">
            {renderEntryMenuButton()}
            {renderIndexNumber()}
            {renderTitleSection()}
            {renderWarningIcon()}
          </Group>

          {/* Expert sidekick selection in mobile */}
          <Box pl="xl">{renderSidekickSelection()}</Box>

          {/* Subtopics below sidekick selection in mobile */}
          {subtopics && <Box pl="xl">{renderSubtopics()}</Box>}
        </Stack>
      ) : (
        <Group wrap="nowrap" align="flex-start">
          {/* Desktop layout - horizontal */}
          <Box style={{ flex: 1 }}>
            <Group gap="xs" mb="sm">
              {renderEntryMenuButton()}
              {renderIndexNumber()}
              <Box style={{ flex: 1 }}>
                {isEditing ? (
                  renderEditInput()
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <Title
                      order={6}
                      onClick={() => {
                        if (canEditName) {
                          setIsEditing(true)
                          setValidationError(null)
                        }
                      }}
                      style={{ cursor: canEditName ? 'pointer' : 'default' }}
                      c={!item.displayName ? 'dimmed' : undefined}>
                      {item.displayName || 'Add topic name'}
                    </Title>
                    <Group gap="xs" wrap="nowrap">
                      {renderModuleCount()}
                      {renderWarningIcon()}
                    </Group>
                  </Group>
                )}
              </Box>
            </Group>
            {renderSubtopics()}
          </Box>

          {/* Expert sidekick selection on right in desktop */}
          {renderSidekickSelection()}
        </Group>
      )}

      <SidekickSelectionModal
        opened={isModalOpen && canChangeSidekick}
        onClose={() => setIsModalOpen(false)}
        options={eskOptions}
        selectedValue={selectedExpertSidekick ?? null}
        onSelect={eskId => {
          if (canChangeSidekick) {
            onExpertSidekickChange?.(item.id ?? '', selectedExpertSidekick ?? null, eskId)
            setIsModalOpen(false)
          }
        }}
        defaultMskSidekick={defaultMskSidekick}
      />
    </Paper>
  )
}
